/**
 * PublicRegistrationForm — anyone can fill this to register as a player for a
 * specific auction. Reached via `/register/{slug}`. On successful submission
 * the entry lands as "pending" in the admin's Registrations tab where an
 * organizer can Approve → creates a Player row in that auction.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { UploadSimple, CheckCircle } from "@phosphor-icons/react";
import { resolveImageUrl } from "@/components/ImageInput";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OPTIONAL_LABELS = {
  father_name: "Father's Name",
  dob: "Date of Birth",
  whatsapp: "WhatsApp Number",
  email: "Email",
  address: "Full Address",
  village: "Village",
  tehsil: "Tehsil",
  district: "District",
  state: "State",
  pincode: "Pincode",
  bowling_style: "Bowling Style",
  location: "Location",
  notes: "Name Of Club",
  total_matches: "Total Matches",
  total_runs: "Total Runs",
  total_wickets: "Total Wickets",
  hundreds: "100s",
  fifties: "50s",
  highest_score: "Highest Score",
  best_bowling: "Best Bowling",
  strike_rate: "Strike Rate",
  economy_rate: "Economy Rate",
  batting_avg: "Batting Average",
  bowling_avg: "Bowling Average",
};

const NUMERIC_FIELDS = new Set([
  "total_matches", "total_runs", "total_wickets", "hundreds", "fifties",
  "strike_rate", "economy_rate", "batting_avg", "bowling_avg",
]);

export default function PublicRegistrationForm() {
  const { slug } = useParams();
  const [auction, setAuction] = useState(null);
  const [formCfg, setFormCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    player_name: "",
    mobile: "",
    role: "Batsman",
    batting_style: "Right Hand",
    bowling_style: "",
    age: "",
    photo_url: "",
    category: "A",
    custom_field_values: {},
    // Iter33: payment fields — filled only when auction.registration_fee > 0
    utr: "",
    payment_screenshot_url: "",
    payment_screenshot_hash: "",
  });
  const [payShotUploading, setPayShotUploading] = useState(false);
  const [utrLocked, setUtrLocked] = useState(false);
  const payFileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/public/register/${slug}`);
      setAuction(data.auction);
      setFormCfg(data.form_config);
    } catch (e) {
      toast.error("Registration form not available");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCustom = (id, v) => setForm((f) => ({ ...f, custom_field_values: { ...f.custom_field_values, [id]: v } }));

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Photo must be an image"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Photo must be < 10 MB"); return; }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/public/register/${slug}/upload`, fd);
      setField("photo_url", data.url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed");
    } finally {
      setPhotoUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handlePayShot = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Screenshot must be an image"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Screenshot must be < 10 MB"); return; }
    setPayShotUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/public/register/${slug}/upload?purpose=payment`, fd);
      setForm((f) => ({ ...f, payment_screenshot_url: data.url, payment_screenshot_hash: data.hash || "", utr: data.extracted_utr || f.utr }));
      setUtrLocked(Boolean(data.extracted_utr));
      toast.success(data.extracted_utr ? `Screenshot uploaded — UTR auto-filled: ${data.extracted_utr}` : "Payment screenshot uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Screenshot upload failed");
    } finally {
      setPayShotUploading(false);
      if (payFileRef.current) payFileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.player_name || !form.mobile || !form.photo_url) {
      toast.error("Name, mobile and photo are required");
      return;
    }
    const feeRequired = Number(auction?.registration_fee || 0) > 0;
    if (feeRequired && (!form.utr.trim() || !form.payment_screenshot_url)) {
      toast.error("UTR and payment screenshot are required for paid registration");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        player_name: form.player_name.trim(),
        mobile: form.mobile.trim(),
        role: form.role,
        photo_url: form.photo_url,
        batting_style: form.batting_style,
        bowling_style: form.bowling_style || undefined,
        age: form.age ? Number(form.age) : undefined,
        category: form.category,
      };
      if (feeRequired) {
        body.utr = form.utr.trim();
        body.payment_screenshot_url = form.payment_screenshot_url;
        body.payment_screenshot_hash = form.payment_screenshot_hash || undefined;
      }
      (formCfg.enabled_optional_fields || []).forEach((f) => {
        if (form[f] === undefined || form[f] === "") return;
        body[f] = NUMERIC_FIELDS.has(f) ? Number(form[f]) : form[f];
      });
      if (formCfg.custom_fields?.length) body.custom_field_values = form.custom_field_values;
      await axios.post(`${API}/public/register/${slug}`, body);
      setDone(true);
      toast.success("Registration submitted — awaiting approval");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-slate-950 grid place-items-center text-white label-mono">Loading…</div>;
  if (!auction) return <div className="min-h-screen bg-slate-950 grid place-items-center text-white">Registration form not found.</div>;
  if (!auction.registration_open) return (
    <div className="min-h-screen bg-slate-950 grid place-items-center p-6">
      <div className="max-w-md w-full bg-red-950/40 border border-red-500/40 rounded p-8 text-center text-white">
        <div className="font-display uppercase text-3xl">Registrations Closed</div>
        <div className="text-red-200 mt-3 text-sm">The organiser has closed registrations for <b>{auction.name}</b>.</div>
      </div>
    </div>
  );
  if (done) return (
    <div className="min-h-screen bg-slate-950 grid place-items-center p-6">
      <Toaster theme="dark" richColors position="top-center" />
      <div className="max-w-md w-full bg-emerald-950/50 border border-emerald-500/40 rounded p-8 text-center text-white">
        <CheckCircle weight="fill" size={64} className="mx-auto text-emerald-400 mb-4" />
        <div className="font-display uppercase text-3xl">Submitted!</div>
        <div className="text-emerald-200 mt-3 text-sm">Your registration for <b>{auction.name}</b> is pending approval. You'll hear from the organiser soon.</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-white py-6" data-testid="public-register-page">
      <Toaster theme="dark" richColors position="top-center" />
      <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-[minmax(280px,360px)_1fr] gap-6">
        {/* Left column — Tournament banner + long description (superadmin-editable) */}
        <aside className="space-y-4" data-testid="reg-tournament-panel">
          {auction.banner_url && (
            <img
              src={resolveImageUrl(auction.banner_url)}
              alt=""
              className="w-full rounded-lg object-cover max-h-72 border border-zinc-800"
              onError={(e) => { e.target.style.display = "none"; }}
              data-testid="reg-banner"
            />
          )}
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5">
            <div className="label-mono text-orange-400 mb-2">TOURNAMENT</div>
            <div className="font-display uppercase text-2xl leading-tight" data-testid="reg-tournament-name">{auction.name}</div>
            {auction.description && (
              <p className="text-sm text-zinc-400 mt-3" data-testid="reg-tournament-desc-short">{auction.description}</p>
            )}
            {auction.registration_description && (
              <div
                className="text-sm text-zinc-300 mt-4 whitespace-pre-wrap leading-relaxed"
                data-testid="reg-tournament-desc-long"
              >{auction.registration_description}</div>
            )}
          </div>
        </aside>

        {/* Right column — the form itself */}
        <div>
          <div className="mb-4">
            <div className="font-display uppercase text-3xl leading-tight" data-testid="reg-heading">Player Registration</div>
            <div className="label-mono mt-1">Fill the form below · Approval by organiser</div>
          </div>

        <form onSubmit={submit} className="space-y-4 bg-zinc-900 border border-zinc-800 rounded p-5" data-testid="register-form">
          <Field label="Full Name *" testId="player-name">
            <input required value={form.player_name} onChange={(e) => setField("player_name", e.target.value)} className="input" data-testid="input-player-name" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Mobile Number *" testId="mobile">
              <input required type="tel" value={form.mobile} onChange={(e) => setField("mobile", e.target.value)} className="input" data-testid="input-mobile" />
            </Field>
            <Field label="Age" testId="age">
              <input type="number" min="10" max="80" value={form.age} onChange={(e) => setField("age", e.target.value)} className="input" data-testid="input-age" />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Role *">
              <select value={form.role} onChange={(e) => setField("role", e.target.value)} className="input" data-testid="select-role">
                {["Batsman", "Bowler", "All-rounder", "Wicket Keeper"].map((r) => <option key={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Batting Style">
              <select value={form.batting_style} onChange={(e) => setField("batting_style", e.target.value)} className="input" data-testid="select-batting-style">
                {["Right Hand", "Left Hand"].map((r) => <option key={r}>{r}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Bowling Style (optional)">
            <input value={form.bowling_style} onChange={(e) => setField("bowling_style", e.target.value)} placeholder="e.g. Right-arm Fast, Off-spin" className="input" data-testid="input-bowling-style" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Category">
              <select value={form.category} onChange={(e) => setField("category", e.target.value)} className="input" data-testid="select-category">
                {["A", "B", "C", "D", "Icon", "Guest"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <div />
          </div>
          <Field label="Photo *" testId="photo">
            <div className="flex items-center gap-3">
              {form.photo_url ? (
                <img src={resolveImageUrl(form.photo_url)} alt="" className="w-20 h-24 rounded object-cover border border-zinc-700" data-testid="photo-preview" onError={(e) => { e.target.style.display = "none"; }} />
              ) : (
                <div className="w-20 h-24 rounded border border-dashed border-zinc-700 grid place-items-center text-zinc-500 text-xs">No photo</div>
              )}
              <div className="flex-1">
                <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} className="hidden" data-testid="input-photo" />
                <button type="button" disabled={photoUploading} onClick={() => fileRef.current?.click()} className="btn-ghost flex items-center gap-2 text-sm">
                  <UploadSimple size={14} /> {photoUploading ? "Uploading…" : (form.photo_url ? "Change photo" : "Upload photo")}
                </button>
                <div className="text-[10px] text-zinc-500 mt-1">JPG / PNG / WEBP · max 10 MB</div>
              </div>
            </div>
          </Field>

          {(formCfg.enabled_optional_fields || []).map((k) => (
            <Field key={k} label={OPTIONAL_LABELS[k] || k}>
              <input
                type={NUMERIC_FIELDS.has(k) ? "number" : "text"}
                step={NUMERIC_FIELDS.has(k) ? "any" : undefined}
                min={NUMERIC_FIELDS.has(k) ? "0" : undefined}
                value={form[k] || ""}
                onChange={(e) => setField(k, e.target.value)}
                className="input"
                data-testid={`input-${k}`}
              />
            </Field>
          ))}
          {(formCfg.custom_fields || []).map((cf) => (
            <Field key={cf.id} label={cf.field_name + (cf.required ? " *" : "")}>
              {cf.field_type === "textarea" ? (
                <textarea value={form.custom_field_values[cf.id] || ""} required={cf.required} onChange={(e) => setCustom(cf.id, e.target.value)} className="input min-h-[80px]" />
              ) : cf.field_type === "select" ? (
                <select value={form.custom_field_values[cf.id] || ""} required={cf.required} onChange={(e) => setCustom(cf.id, e.target.value)} className="input">
                  <option value="">Select…</option>
                  {(Array.isArray(cf.options) ? cf.options : String(cf.options || "").split(",").map((s) => s.trim()).filter(Boolean)).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={cf.field_type === "number" ? "number" : "text"} value={form.custom_field_values[cf.id] || ""} required={cf.required} onChange={(e) => setCustom(cf.id, e.target.value)} className="input" />
              )}
            </Field>
          ))}

          {Number(auction?.registration_fee || 0) > 0 && (
            <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4 space-y-3" data-testid="payment-section">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="label-mono text-emerald-300">REGISTRATION FEE</div>
                  <div className="text-2xl font-black text-white leading-none mt-1">₹ {Number(auction.registration_fee).toLocaleString()}</div>
                </div>
                <div className="text-[10px] text-zinc-400 uppercase tracking-widest">Scan · Pay · Upload screenshot</div>
              </div>
              {auction.payment_qr_url && (
                <div className="rounded bg-white p-3 flex justify-center">
                  <img src={resolveImageUrl(auction.payment_qr_url)} alt="Payment QR" className="max-w-[220px] w-full h-auto" data-testid="payment-qr" onError={(e) => { e.target.style.display = "none"; }} />
                </div>
              )}
              <div>
                <label className="label-mono block mb-1">UTR / Transaction ID *</label>
                <input
                  type="text"
                  value={form.utr}
                  onChange={(e) => setField("utr", e.target.value)}
                  required
                  disabled={utrLocked}
                  placeholder="e.g. 43219098xxxx"
                  className="input disabled:opacity-70 disabled:cursor-not-allowed"
                  data-testid="input-utr"
                />
                {utrLocked ? (
                  <p className="text-[10px] text-emerald-400 mt-1" data-testid="utr-autofill-hint-locked">Auto-filled from your payment screenshot — locked. Upload a new screenshot to change it.</p>
                ) : (
                  <p className="text-[10px] text-zinc-500 mt-1" data-testid="utr-autofill-hint-idle">Auto-fills when you upload the payment screenshot below.</p>
                )}
              </div>
              <div>
                <label className="label-mono block mb-1">Payment Screenshot *</label>
                <input type="file" accept="image/*" ref={payFileRef} onChange={handlePayShot} className="hidden" data-testid="input-payment-shot" />
                <div className="flex items-center gap-3">
                  {form.payment_screenshot_url ? (
                    <img src={resolveImageUrl(form.payment_screenshot_url)} alt="" className="w-20 h-24 rounded object-cover border border-zinc-700" data-testid="payment-preview" onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div className="w-20 h-24 rounded border border-dashed border-zinc-700 flex items-center justify-center text-[10px] text-zinc-500 text-center">No screenshot</div>
                  )}
                  <button type="button" disabled={payShotUploading} onClick={() => payFileRef.current?.click()} className="btn-ghost flex items-center gap-2 text-sm" data-testid="upload-payment-shot-btn">
                    <UploadSimple size={14} /> {payShotUploading ? "Reading UTR…" : (form.payment_screenshot_url ? "Change" : "Upload screenshot")}
                  </button>
                  {form.payment_screenshot_url && <CheckCircle weight="fill" className="text-emerald-500" size={20} />}
                </div>
                <p className="text-[10px] text-zinc-500 mt-1">Duplicate UTRs or duplicate screenshots are rejected. Use your own real payment proof.</p>
              </div>
            </div>
          )}

          <button type="submit" disabled={submitting || photoUploading || payShotUploading} className="btn-primary w-full !py-3" data-testid="submit-registration">
            {submitting ? "Submitting…" : "Submit Registration"}
          </button>
        </form>
        </div>

        <style>{`
          .input {
            width: 100%;
            background: #0F172A;
            border: 1px solid #334155;
            color: #F1F5F9;
            padding: 8px 12px;
            border-radius: 4px;
            outline: none;
          }
          .input:focus { border-color: #10B981; }
        `}</style>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="label-mono text-zinc-400 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
